<template>
  <div class="view-tasklists account-lookup">
    <user-menu />
    <div class="wrapper">
      <masteradmin-sidebar-menu v-if="masterAdmin == true" />
      <div class="content-wrapper">
        <div class="container-wrapper center">

          <div class="cs-header">
            <h3 class="d-inline heading">Customer Success Report</h3>
            <div class="cs-actions">
              <button type="button" class="btn btn-sm btn-outline-secondary mr-2" :disabled="reportBusy" @click="exportCsv()">
                <i class="fa fa-download"></i> Export CSV
              </button>
              <button v-if="pdf_loading == true" type="button" class="btn btn-sm cs-btn-blue">
                Loading ...
              </button>
              <button v-else type="button" class="btn btn-sm cs-btn-blue" @click="pdfDownload()">
                <i class="fa fa-file-text-o"></i> Print / Save PDF
              </button>
            </div>
          </div>

          <!-- Filters -->
          <div class="cs-card cs-filter">
            <div class="cs-filter-top">
              <div class="cs-filter-group">
                <label>Date range</label>
                <select v-model="dateRange" @change="onDateRangeChange()" class="form-control form-control-sm">
                  <option v-for="o in dateRanges" :key="o" :value="o">{{ o }}</option>
                </select>
              </div>
              <div class="cs-filter-group">
                <label>Compare to</label>
                <select v-model="compareTo" @change="loadReport()" class="form-control form-control-sm">
                  <option v-for="o in compareOptions" :key="o" :value="o">{{ o }}</option>
                </select>
              </div>
              <div v-if="dateRange === 'Custom Dates'" class="cs-custom-dates">
                <div class="cs-filter-group">
                  <label>From Date</label>
                  <input type="date" v-model="fromDate" :max="toDate || today" class="form-control form-control-sm" />
                </div>
                <div class="cs-filter-group">
                  <label>To Date</label>
                  <input type="date" v-model="toDate" :min="fromDate" :max="today" class="form-control form-control-sm" />
                </div>
                <div class="cs-filter-group cs-apply">
                  <button type="button" class="btn btn-sm cs-btn-blue" :disabled="!customValid" @click="applyCustomDates()">Apply Dates</button>
                </div>
                <div v-if="customError" class="cs-down cs-custom-error">{{ customError }}</div>
              </div>
              <div class="cs-period">
                <small>Selected reporting period</small>
                <strong>{{ period }}</strong>
              </div>
            </div>
            <div class="cs-filter-bottom">
              <div>
                <strong>Comparing to {{ compareTo }}: {{ comparePeriod }}</strong>
                <div class="cs-muted">Same-length range, shifted back from the selected reporting period.</div>
              </div>
              <div class="cs-legend">
                <span class="cs-up"><i class="fa fa-arrow-up"></i> Increase</span>
                <span class="cs-down"><i class="fa fa-arrow-down"></i> Decrease</span>
                <span class="cs-muted">— No change</span>
                <span class="cs-muted-unavailable">N/A: unavailable</span>
              </div>
            </div>
          </div>

          <!-- AI summary (LUNA) -->
          <div class="cs-card cs-summary">
            <div class="cs-summary-head">
              <div class="cs-summary-title"><span class="cs-summary-icon"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-compass" aria-hidden="true" data-replit-metadata="artifacts/mentor-mentee-dms-app/src/components/CustomerSuccessSummary.tsx:52:63" data-component-name="Compass"><path d="m16.24 7.76-1.804 5.411a2 2 0 0 1-1.265 1.265L7.76 16.24l1.804-5.411a2 2 0 0 1 1.265-1.265z"></path><circle cx="12" cy="12" r="10"></circle></svg></span> CUSTOMER SUCCESS SUMMARY</div>
              <span v-if="summary.data && !summary.hidden" class="cs-updated"><i class="fa fa-circle"></i> Updated <strong>{{ summary.data.generatedAt }}</strong></span>
              <span v-else class="cs-updated">&nbsp;</span>
              <button type="button" class="btn btn-sm btn-outline-primary " @click="summary.hidden = !summary.hidden" v-if="summary.hidden">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye" data-replit-metadata="artifacts/mentor-mentee-dms-app/src/components/CustomerSuccessSummary.tsx:65:63" data-component-name="Eye" aria-hidden="true"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path><circle cx="12" cy="12" r="3"></circle></svg> Show Customer Success Summary
              </button>
              <button type="button" class="btn btn-sm btn-outline-primary " @click="summary.hidden = !summary.hidden" v-else>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-off" data-replit-metadata="artifacts/mentor-mentee-dms-app/src/components/CustomerSuccessSummary.tsx:65:21" data-component-name="EyeOff" aria-hidden="true"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"></path><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"></path><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"></path><path d="m2 2 20 20"></path></svg> Hide Customer Success Summary
              </button>
            </div>
            <template v-if="!summary.hidden">
              <div v-if="summary.loading" class="cs-muted">Generating summary...</div>
              <div v-else-if="summary.error" class="cs-down">
                {{ summary.error }} <a href="#" @click.prevent="loadSummary()">Retry</a>
              </div>
              <template v-else-if="summary.data">
                <div class="cs-summary-sel">
                  Report selection: <strong>{{ summary.data.period }}</strong> · {{ summary.data.compareTo }}
                  <strong>{{ summary.data.comparePeriod }}</strong>
                </div>
                <div class="cs-summary-headline">{{ summary.data.headline }}</div>
                <ul class="cs-summary-list">
                  <li v-for="item in summary.data.items" :key="item.id" :class="'cs-sum-' + item.status">
                    <strong>{{ item.name }}:</strong> {{ item.text }}
                  </li>
                </ul>
              </template>
            </template>
          </div>

          <!-- Tables -->
          <div class="cs-grid">
            <div v-for="sec in sections" :key="sec.title" class="cs-card cs-table-card" :class="{ 'cs-full': sec.full }">
              <h5 class="cs-section-title">{{ sec.title }}</h5>
              <div class="cs-table-wrap" @scroll="hideTip()">
                <table class="cs-table">
                  <thead>
                    <tr>
                      <th class="cs-sortable" @click="sortBy(sec, 'name')">Account Name:<i :class="sortIcon(sec, 'name')"></i></th>
                      <th v-for="c in sec.cols" :key="c.key" class="cs-sortable" @click="sortBy(sec, c.key)">{{ c.label }}<i :class="sortIcon(sec, c.key)"></i></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="state[sec.dataKey].loading">
                      <td :colspan="sec.cols.length + 1" class="cs-muted">Loading...</td>
                    </tr>
                    <tr v-else-if="state[sec.dataKey].error">
                      <td :colspan="sec.cols.length + 1" class="cs-down">{{ state[sec.dataKey].error }}</td>
                    </tr>
                    <tr v-else-if="!rowsFor(sec).length">
                      <td :colspan="sec.cols.length + 1" class="cs-muted">No active accounts for this period.</td>
                    </tr>
                    <template v-else>
                      <tr v-for="a in rowsFor(sec)" :key="a.id">
                        <td><a :href="accountDetailUrl(a.id)" target="_blank" rel="noopener" @click.prevent="openAccount(a.id)">{{ a.name }}</a></td>
                        <td v-for="c in sec.cols" :key="c.key" class="cs-cell"
                            :class="(sec.noCompare || c.noCompare) ? 'neutral' : (a[c.key].v !== 'N/A' ? deltaState(a[c.key].d) : '')"
                            @mouseenter="showTip($event, a, c, sec)" @mouseleave="hideTip()">
                          <template v-if="c.date || a[c.key].v === 'N/A'">
                            {{ a[c.key].v }}
                          </template>
                          <template v-else>
                            {{ a[c.key].v }}{{ c.suffix || '' }}
                          </template>
                          <span v-if="!(sec.noCompare || c.noCompare) && a[c.key].v !== 'N/A'" :class="deltaClass(a[c.key].d)">
                            <i :class="deltaIcon(a[c.key].d)"></i>
                            {{ deltaText(a[c.key].d, c) }}
                          </span>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Hover popup (fixed so the scrolling table does not clip it) -->
          <div v-if="tip" class="cs-tip" :class="tip.state" :style="{ top: tip.top + 'px', left: tip.left + 'px' }">
            <div class="cs-tip-name">{{ tip.name }}</div>
            <div class="cs-tip-label">{{ tip.label }}</div>
            <div class="cs-tip-row"><span>Current</span><strong>{{ tip.current }}</strong></div>
            <div v-if="tip.previous !== null" class="cs-tip-row"><span>Previous</span><strong>{{ tip.previous }}</strong></div>
            <div v-if="tip.compare !== false" class="cs-tip-row">
              <span>Change</span>
              <strong :class="deltaClass(tip.d)"><i :class="deltaIcon(tip.d)"></i> {{ tip.change }}</strong>
            </div>
            <div v-if="tip.compare !== false" class="cs-tip-foot">{{ compareTo }}: {{ comparePeriod }}</div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserMenu from '../../shared/user_menu.vue';
import MasteradminSidebarMenu from '../../shared/masteradmin_sidebar_menu.vue';
import axios from 'axios';

// helper: metric with value v and delta d
// v = value, d = change, p = previous value (optional; when set the popup shows it as is)
const m = (v, d, p) => (p === undefined ? { v, d } : { v, d, p });

// helper: build a metric from an API {current, previous} pair
const pct = (n, d) => (d ? Math.round((n / d) * 1000) / 10 : 0);
const diff = (c, p) => Math.round((c - p) * 10) / 10;
const fromApi = (o) => (o ? m(o.current, diff(o.current, o.previous)) : m(0, null));

export default {
  components: { UserMenu, MasteradminSidebarMenu },
  data() {
    return {
      masterAdmin: false,
      dateRange: 'Trailing 7 Days',
      compareTo: 'Last Week',
      dateRanges: ['Trailing 7 Days', 'Trailing 14 Days', 'Trailing 30 Days', 'Trailing Quater', 'Year to Date (YTD)', 'Custom Dates'],
      compareOptions: ['Yesterday', 'Last Week', 'Last Month', 'Last Quater'],
      period: '',
      range: null, // ISO dates of the loaded report, used by the CSV export
      comparePeriod: '',
      fromDate: '',
      toDate: '',
      state: {
        accountInfo: { loading: false, error: '' },
        loginActivity: { loading: false, error: '' },
        technicianProgress: { loading: false, error: '' },
        repetitions: { loading: false, error: '' },
        pathwayCompletion: { loading: false, error: '' },
      },
      tip: null,
      summary: { data: null, loading: false, error: '', hidden: false, requestId: 0 },
      // per-panel sort; key null = no active sort (API order, by account name)
      sort: {
        accountInfo: { key: null, dir: 'asc' },
        loginActivity: { key: null, dir: 'asc' },
        technicianProgress: { key: null, dir: 'asc' },
        repetitions: { key: null, dir: 'asc' },
        pathwayCompletion: { key: null, dir: 'asc' },
      },
      accountInfo: [],
      sections: [
        { title: 'Account Info', dataKey: 'accountInfo', cols: [
          { key: 'techs', label: '# of Technicians:' },
          { key: 'mentors', label: '# of Mentors:' },
          { key: 'loggedIn', label: '# of Technicians Logged In:' },
          { key: 'loginPct', label: 'Login %:', suffix: '%', unit: '%' },
        ] },
        { title: 'Login Activity', dataKey: 'loginActivity', cols: [
          { key: 'freq', label: 'Login Frequency:' },
          { key: 'lastSm', label: 'Last SM Login:', date: true, noCompare: true },
          { key: 'lastMentor', label: 'Last Mentor Login:', date: true, noCompare: true },
          { key: 'lastTech', label: 'Last Technician Login:', date: true, noCompare: true },
        ] },
        { title: 'Technician Progress', dataKey: 'technicianProgress', cols: [
          { key: 'withReps', label: 'Technicians w/ Reps:' },
          { key: 'withSkills', label: 'Technicians w/ Skills Completed:' },
          { key: 'withComps', label: 'Technicians w/ Comps. Earned:' },
        ] },
        { title: 'Repetitions', dataKey: 'repetitions', cols: [
          { key: 'totalReps', label: 'Total Reps:' },
          { key: 'notReviewed', label: 'Reps Not Reviewed:' },
          { key: 'approved', label: 'Reps Approved:' },
          { key: 'approvalPct', label: 'Approval %:', suffix: '%', unit: '%' },
        ] },
        { title: 'Pathway Completion Breakdown', dataKey: 'pathwayCompletion', full: true, noCompare: true, cols: [
          { key: 'lt32', label: '<32% Complete:' },
          { key: 'mid33to60', label: '33%-60% Complete:' },
          { key: 'gt60', label: '>60% Complete:' },
        ] },
      ],
      loginActivity: [],
      technicianProgress: [],
      repetitions: [],
      pathwayCompletion: [],
      pdf_file_name: '',
      apiUrl: process.env.API_ENDPOINT,
      pdf_loading: false,
    };
  },
  computed: {
    // local YYYY-MM-DD (not toISOString, which is UTC and can be a day off)
    today() {
      return this.toInputDate(new Date());
    },
    customError() {
      if (!this.fromDate || !this.toDate) return 'Select both From and To dates.';
      if (this.toDate > this.today || this.fromDate > this.today) return 'Future dates are not allowed.';
      if (this.fromDate > this.toDate) return 'From Date cannot be after To Date.';
      return '';
    },
    // true while loading, or when there is nothing to export
    reportBusy() {
      return Object.keys(this.state).some(k => this.state[k].loading) || !this.range || !this.accountInfo.length;
    },
    customValid() {
      return !this.customError;
    },
  },
  methods: {
    deltaClass(d) {
      if (d === null || d === undefined) return 'cs-muted';
      return d > 0 ? 'cs-up' : d < 0 ? 'cs-down' : 'cs-muted';
    },
    deltaIcon(d) {
      if (d > 0) return 'fa fa-arrow-up';
      if (d < 0) return 'fa fa-arrow-down';
      return '';
    },
    deltaText(d, col) {
      if (d === null || d === undefined) return 'N/A';
      const unit = col.unit || '';
      if (d === 0) return '— 0' + (unit === '%' ? '%' : '');
      return (d > 0 ? '+' : '') + d + unit;
    },
    deltaState(d) {
      return d > 0 ? 'up' : d < 0 ? 'down' : 'zero';
    },
    showTip(e, a, c, sec) {
      const cell = a[c.key];
      const noCompare = !!((sec && sec.noCompare) || c.noCompare); // snapshot panel or column: no previous / change
      // plain N/A cells (no data for the column yet) have no popup; date cells keep it, since the previous date may exist
      if (cell.v === 'N/A' && !c.date) {
        this.tip = null;
        return;
      }
      const suffix = c.date ? '' : (c.suffix || '');
      const numeric = typeof cell.v === 'number' && typeof cell.d === 'number';
      const r = e.currentTarget.getBoundingClientRect();
      const w = 260; // popup width (matches .cs-tip)
      const h = 190; // approx. popup height, used only to keep it on screen
      let left = r.right + 8;
      if (left + w > window.innerWidth - 8) left = Math.max(8, r.left - w - 8);
      const top = Math.max(8, Math.min(r.top, window.innerHeight - h - 8));
      if (noCompare) {
        this.tip = { name: a.name, label: c.label.replace(/:$/, ''), current: cell.v + suffix, previous: null, compare: false, state: 'neutral', d: null, change: '', top, left };
        return;
      }
      this.tip = {
        name: a.name,
        label: c.label.replace(/:$/, ''),
        current: cell.v === 'N/A' ? 'N/A' : cell.v + suffix,
        previous: cell.p !== undefined
          ? (cell.p === null ? 'N/A' : cell.p + suffix)
          : (!c.date && numeric ? (Math.round((cell.v - cell.d) * 10) / 10) + suffix : null),
        change: this.deltaText(cell.d, c),
        d: cell.d,
        state: this.deltaState(cell.d),
        top,
        left,
      };
    },
    hideTip() {
      this.tip = null;
    },
    printReport() {
      window.print();
    },
    toInputDate(d) {
      const p = (n) => (n < 10 ? '0' + n : '' + n);
      return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
    },
    formatInputDate(s) {
      const [y, mo, d] = s.split('-').map(Number);
      return new Date(y, mo - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    onDateRangeChange() {
      if (this.dateRange === 'Custom Dates') {
        // wait for "Apply Dates"; default to the last 7 days
        const start = new Date();
        start.setDate(start.getDate() - 6);
        if (!this.fromDate) this.fromDate = this.toInputDate(start);
        if (!this.toDate) this.toDate = this.today;
        return;
      }
      this.loadReport();
    },
    applyCustomDates() {
      if (this.toDate > this.today) this.toDate = this.today;
      if (!this.customValid) return;
      this.period = this.formatInputDate(this.fromDate) + ' – ' + this.formatInputDate(this.toDate);
      this.loadReport();
    },
    accountDetailUrl(id) {
      return this.$router.resolve({ name: 'accountlookupdetail', params: { account_id: id } }).href;
    },
    openAccount(id) {
      window.open(this.accountDetailUrl(id), '_blank');
    },
    sortBy(sec, key) {
      const cur = this.sort[sec.dataKey];
      this.$set(this.sort, sec.dataKey, cur.key === key
        ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' });
      this.hideTip();
    },
    sortIcon(sec, key) {
      const cur = this.sort[sec.dataKey];
      if (cur.key !== key) return 'fa fa-sort';
      return cur.dir === 'asc' ? 'fa fa-sort-asc active' : 'fa fa-sort-desc active';
    },
    // numeric value used for sorting; null when the cell is N/A or missing
    sortValue(row, sec, key) {
      if (key === 'name') return String(row.name || '').toLowerCase();
      const col = sec.cols.find(c => c.key === key);
      const cell = row[key];
      if (!cell || cell.v === 'N/A' || cell.v === null || cell.v === undefined) return null;
      if (col && col.date) {
        // exact login time (UTC 'YYYY-MM-DD HH:mm:ss') when the API sends it, otherwise the MM/DD/YYYY date
        let t = cell.s ? Date.parse(cell.s.replace(' ', 'T') + 'Z') : NaN;
        if (isNaN(t)) {
          const [mo, d, y] = String(cell.v).split('/').map(Number);
          t = new Date(y, mo - 1, d).getTime();
        }
        return isNaN(t) ? null : t;
      }
      return cell.v;
    },
    rowsFor(sec) {
      const rows = this[sec.dataKey].slice();
      const { key, dir } = this.sort[sec.dataKey];
      const sign = dir === 'asc' ? 1 : -1;
      const byName = (a, b) => String(a.name || '').toLowerCase().localeCompare(String(b.name || '').toLowerCase());
      return rows.sort((a, b) => {
        const x = this.sortValue(a, sec, key);
        const y = this.sortValue(b, sec, key);
        // N/A always last, whichever direction
        if (x === null && y === null) return byName(a, b);
        if (x === null) return 1;
        if (y === null) return -1;
        const c = typeof x === 'string' ? x.localeCompare(y) : x - y;
        return c !== 0 ? sign * c : byName(a, b);
      });
    },
    // CSV layout: header block, then per panel a title row, a header row (label + Previous/Trend/Change) and one row per account
    exportCsv() {
      if (this.reportBusy) return;
      const NA = 'N/A';
      const us = (iso) => { const [y, mo, d] = iso.split('-'); return mo + '/' + d + '/' + y; };
      const esc = (v) => {
        const t = v === null || v === undefined ? '' : String(v);
        return /[",\r\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t;
      };
      const width = 1 + 4 * Math.max.apply(null, this.sections.map(sec => sec.cols.length));
      const lines = [];
      const add = (cells) => {
        const row = cells.slice();
        while (row.length < width) row.push('');
        lines.push(row.map(esc).join(','));
      };
      const change = (d, col) => {
        if (d === null || d === undefined) return NA;
        const sign = d > 0 ? '+' : '';
        if (col.date) return sign + d + ' days';
        if (col.suffix === '%') return sign + d + '%';
        return String(d);
      };

      add(['Customer Success Report']);
      add(['From Date', us(this.range.from)]);
      add(['To Date', us(this.range.to)]);
      add(['Compare to', this.range.compareTo]);
      add(['Comparison From Date', us(this.range.cFrom)]);
      add(['Comparison To Date', us(this.range.cTo)]);
      add([]);
      add([]);

      this.sections.forEach((sec, i) => {
        if (i) add([]);
        add([sec.title]);
        const head = ['Account Name:'];
        sec.cols.forEach(c => ((sec.noCompare || c.noCompare) ? head.push(c.label) : head.push(c.label, c.label + ' Previous', c.label + ' Trend', c.label + ' Change')));
        add(head);
        this.rowsFor(sec).forEach(a => {
          const row = [a.name];
          sec.cols.forEach(c => {
            const cell = a[c.key];
            const suffix = c.date ? '' : (c.suffix || '');
            if (sec.noCompare || c.noCompare) {
              row.push(cell && cell.v !== undefined ? cell.v + suffix : NA); // snapshot panel: value only
              return;
            }
            if (!cell || cell.v === NA) {
              row.push(NA, cell && cell.p ? cell.p : NA, NA, NA);
              return;
            }
            let previous = NA;
            if (cell.p !== undefined) previous = cell.p === null ? NA : cell.p + suffix;
            else if (typeof cell.v === 'number' && typeof cell.d === 'number') previous = (Math.round((cell.v - cell.d) * 10) / 10) + suffix;
            const trend = cell.d === null || cell.d === undefined ? NA : (cell.d > 0 ? 'Up' : cell.d < 0 ? 'Down' : 'No change');
            row.push(cell.v + suffix, previous, trend, change(cell.d, c));
          });
          add(row);
        });
      });

      const blob = new Blob([lines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL || window.webkitURL;
      const link = url.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('download', 'customer-success-' + this.range.from + '-to-' + this.range.to + '.csv');
      a.setAttribute('href', link);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      url.revokeObjectURL(link);
    },
    pdfDownload() {
      this.pdf_loading = true;
      const custom = this.dateRange === 'Custom Dates';
      // account ids per panel in the order currently shown, so the PDF follows the on-screen sort
      const rowOrder = {};
      this.sections.forEach(sec => { rowOrder[sec.dataKey] = this.rowsFor(sec).map(a => a.id); });
      axios.post('/desktop/masteradmin-customer-success-report-pdf', {
        date_range: this.dateRange,
        compare_to: this.compareTo,
        from_date: custom ? this.fromDate : null,
        to_date: custom ? this.toDate : null,
        row_order: rowOrder,
        summary: this.summary.data,
      })
        .then(response => {
          this.pdf_file_name = response.data.pdf_file_name;
          var url = this.apiUrl + '/custom/pdf-view/' + this.pdf_file_name;
          //Create XMLHTTP Request.
          var req = new XMLHttpRequest();
          req.open("GET", url, true);
          req.responseType = "blob";
          req.onload = function () {
          //Convert the Byte Data to BLOB object.
          var blob = new Blob([req.response], { type: "application/octetstream" });

          //Check the Browser type and download the File.
          var isIE = false || !!document.documentMode;
          if (isIE) {
              window.navigator.msSaveBlob(blob, "customer-success-report.pdf");
          } else {
              var url = window.URL || window.webkitURL;
              var link = url.createObjectURL(blob);
              var a = document.createElement("a");
              a.setAttribute("download", "customer-success-report.pdf");
              a.setAttribute("href", link);
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
          }
        };
        req.send();
        this.pdf_loading = false;
        return false;

        })
        .catch(error => {
        })  
    },
    // AI summary (LUNA) for the same filters; only the latest request's answer is kept
    loadSummary() {
      const custom = this.dateRange === 'Custom Dates';
      const requestId = ++this.summary.requestId;
      this.summary.loading = true;
      this.summary.error = '';
      axios.post('/desktop/masteradmin-customer-success-summary', {
        date_range: this.dateRange,
        compare_to: this.compareTo,
        from_date: custom ? this.fromDate : null,
        to_date: custom ? this.toDate : null,
      })
        .then(response => {
          if (requestId !== this.summary.requestId) return;
          this.summary.data = response.data;
        })
        .catch(() => {
          if (requestId !== this.summary.requestId) return;
          this.summary.data = null;
          this.summary.error = 'Unable to generate the summary.';
        })
        .finally(() => {
          if (requestId === this.summary.requestId) this.summary.loading = false;
        });
    },
    // Single request feeds all four panels. Active accounts only; technicians = ROLE_MENTEE/ROLE_APPRENTICE,
    // mentors = ROLE_MENTOR, counts by accounts_users.activated_date, logins by fos_user.user_last_login (computed by the API).
    loadReport() {
      const keys = Object.keys(this.state);
      keys.forEach(k => { this.state[k].loading = true; this.state[k].error = ''; });
      const custom = this.dateRange === 'Custom Dates';
      axios.post('/desktop/masteradmin-customer-success-report', {
        date_range: this.dateRange,
        compare_to: this.compareTo,
        from_date: custom ? this.fromDate : null,
        to_date: custom ? this.toDate : null,
      })
        .then(response => {
          const d = response.data.data || {};
          if (d.period) this.period = d.period;
          if (d.comparePeriod) this.comparePeriod = d.comparePeriod;
          this.range = {
            from: d.fromDate, to: d.toDate, cFrom: d.compareFromDate, cTo: d.compareToDate,
            compareTo: d.compareTo || this.compareTo,
          };
          const mapRows = (rows, dateKeys = []) => (rows || []).map(r => {
            const row = { id: r.id, name: r.name };
            Object.keys(r).forEach(k => {
              if (k === 'name' || k === 'id') return;
              if (r[k] === null || typeof r[k] !== 'object') {
                row[k] = m(r[k] === null || r[k] === undefined ? 'N/A' : r[k], null);
                return;
              }
              if (r[k].count !== undefined) {
                row[k] = m(r[k].count, r[k].change, r[k].compareCount === undefined ? undefined : r[k].compareCount);
                if (r[k].sort) row[k].s = r[k].sort; // exact time, used only for sorting date columns
                return;
              }
              row[k] = dateKeys.indexOf(k) > -1
                ? m(r[k].current, r[k].previous === undefined ? null : r[k].previous)
                : fromApi(r[k]);
            });
            return row;
          });
          this.accountInfo = mapRows(d.accountInfo);
          this.loginActivity = mapRows(d.loginActivity, ['lastSm', 'lastMentor', 'lastTech']);
          this.technicianProgress = mapRows(d.technicianProgress);
          this.repetitions = mapRows(d.repetitions);
          this.pathwayCompletion = mapRows(d.pathwayCompletion);
          this.loadSummary();
        })
        .catch(error => {
          const res = error.response;
          const msg = res && res.status === 400 && res.data && res.data.message
            ? res.data.message
            : 'Unable to load the report. Please try again.';
          keys.forEach(k => { this.state[k].error = msg; });
        })
        .finally(() => {
          keys.forEach(k => { this.state[k].loading = false; });
        });
    },
  },
  created() {
    var roles = this.$store.state.roles;
    if (roles.indexOf('ROLE_MASTER_ADMIN') > -1) {
      this.masterAdmin = true;
    }
    this.loadReport();
    window.addEventListener('scroll', this.hideTip, true);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.hideTip, true);
  },
};
</script>

<style scoped>
  .cs-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
  .cs-btn-blue { background: #1a8fd1; color: #fff; border-color: #1a8fd1; border-radius: 3px;}
  .cs-btn-blue:hover { background: #167ab3; color: #fff; }
  .cs-btn-outline { background: #fff; color: #1a8fd1; border: 1px solid #1a8fd1; }
  .cs-card { background: #fff; border: 1px solid #e3e8ec; border-radius: 4px; margin-bottom: 16px; }
  .cs-filter { border-left: 3px solid #00ACEE; }
  .cs-filter-top { display: flex; align-items: flex-end; padding: 12px 14px; flex-wrap: wrap; }
  .cs-filter-group { margin-right: 20px; min-width: 175px; }
  .cs-filter-group label { font-size: 14px; margin-bottom: 2px; color: #555; padding-top: 0; }
  .cs-custom-dates { display: flex; flex-wrap: wrap; }
  .cs-custom-dates .cs-filter-group { min-width: 150px; }
  .cs-custom-dates .cs-apply { min-width: 0; margin-top: 25px; }
  .cs-custom-error { font-size: 14px; margin-left: 4px; padding-bottom: 6px; }
  .cs-period { margin-left: auto; display: flex; flex-direction: column; font-size: 13px; }
  .cs-period small { color: #777; font-size: 13px; }
  .cs-filter-bottom { display: flex; justify-content: space-between; padding: 8px 14px; background: #f4f8fa; border-top: 1px solid #e3e8ec; font-size: 14px; flex-wrap: wrap; }
  .cs-legend span { margin-left: 14px; }
  .cs-muted { color: #8a949c; font-size: 14px; }
  .cs-up { color: #1e8e3e; font-size: 13px; }
  .cs-down { color: #d93025; font-size: 13px; }
  .cs-summary { padding: 16px 20px; border-left: 3px solid #00ACEE; background: #f5fafd; font-size: 13px; }
  .cs-summary-head { display: flex; align-items: center; margin-bottom: 6px; }
  .cs-summary-title { display: flex; align-items: center; gap: 10px;font-weight: 700; letter-spacing: 1px; font-size: 14px; margin-right: 10px; }
  .cs-summary-title .fa { color: #00ACEE; margin-right: 6px; }
  .cs-badge { border: 1px solid #cfd8de; border-radius: 10px; padding: 1px 8px; font-size: 14px; background: #fff; }
  .cs-updated { margin-left: auto; font-size: 13px; color: #637a85; }
  .cs-summary-head .cs-updated + .cs-summary-toggle { margin-left: 12px; }
  .cs-updated .fa { color: #1e8e3e; font-size: 10px; margin-right: 4px; }
  .cs-summary-toggle { margin-left: auto; }
  .cs-summary-list { margin: 0; padding-left: 20px; }
  .cs-summary-list li { margin-bottom: 6px; }
  .cs-sum-ok { color: #1e8e3e; }
  .cs-sum-attention { color: #d93025; }
  .cs-summary-sel { margin-bottom: 10px; }
  .cs-summary-headline { font-weight: 700; margin: 8px 0; }
  .cs-grid { display: grid; grid-template-columns: 1fr 1fr; grid-gap: 16px; }
  .cs-full { grid-column: 1 / -1; }
  .cs-table-card { padding: 14px; margin-bottom: 0; min-width: 0; }
  .cs-section-title { font-size: 14px; font-weight: 700; margin-bottom: 12px; }
  .cs-table-wrap { overflow-x: auto; }
  .cs-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .cs-table thead th { background: #1a8fd1; color: #fff; padding: 10px 6px; font-size: 12px; white-space: nowrap; }
  .cs-sortable { cursor: pointer; user-select: none; }
  .cs-table thead th .fa.active { opacity: 1; }
  .cs-table thead th .fa { float: right; margin-left: 8px; opacity: .7; }
  .cs-table tbody td { padding: 12px 6px; border-bottom: 1px solid #e3e8ec; white-space: nowrap; }
  .cs-table tbody tr:nth-child(odd) { background: #f1f3f4; }
  .cs-table tbody td a { color: #1a73c8; text-decoration: underline; font-weight: 600; }
  .cs-cell { cursor: default; }
  .cs-cell.neutral:hover { background: #eef6fb; box-shadow: inset 0 0 0 1px #1a8fd1; }
  .cs-cell.up:hover { background: #edf9f0; box-shadow: 0 4px 18px #1d3e5726; }
  .cs-cell.down:hover { background: #fff0ee; box-shadow: inset 0 0 0 1px #1d3e5726; }
  .cs-cell.zero:hover { background: #edf2f6; box-shadow: inset 0 0 0 1px #b7c6d0; }
  .cs-tip { position: fixed; z-index: 2000; width: 260px; padding: 14px 16px; background: #fff; border: 1px solid #cfd8de; border-radius: 6px; box-shadow: 0 6px 20px rgba(0,0,0,.15); pointer-events: none; font-size: 13px; }
  .cs-tip.up { border-color: #98cfa8; background: #edf9f0; box-shadow: 0 4px 18px #1d3e5726; }
  .cs-tip.down { border-color: #eba8a1; background: #fdecea; box-shadow: inset 0 0 0 1px #1d3e5726; }
  .cs-tip.zero { border-color: #d7dfe3; background: #edf2f6; box-shadow: inset 0 0 0 1px #b7c6d0; }
  .cs-tip-name { font-weight: 700; font-size: 15px; }
  .cs-tip-label { color: #666; margin-bottom: 8px; }
  .cs-tip-row { display: flex; justify-content: space-between; padding: 2px 0; }
  .cs-tip-row span { color: #666; }
  .cs-tip-row .cs-up, .cs-tip-row .cs-down, .cs-tip-row .cs-muted { font-size: 13px; }
  .cs-tip-foot { margin-top: 8px; padding-top: 8px; border-top: 1px solid #e3e8ec; color: #555; }
  @media (max-width: 1200px) { .cs-grid { grid-template-columns: 1fr; } }
  @media print { .cs-actions { display: none; } }
  .btn-outline-primary {
    color: #007fb2;
    background: #fff;
    border: 1px solid #00acee;
    min-height: 40px;
    padding: 0px 16px;
    border-radius: 6px;
    margin-left: 20px;
    font-weight: 600;
    outline: none;
  }
  .btn-outline-primary:hover, .btn-outline-primary:active, .btn-outline-primary:focus, .btn-outline-primary:not(:disabled):not(.disabled):active {
    color: #007fb2;
    background: #eaf7fd !important;
    border: 1px solid #00acee;
    box-shadow: none;
  }
  .cs-summary-icon {
    width: 34px;
    height: 34px;
    display: grid;
    place-items: center;
    border-radius: 8px;
    color: #0089bf;
    background: #e1f4fc;
  }
  .form-control-sm {
    height: 35px;
  }
  .cs-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-left: auto;
  }
  .cs-updated strong {
    color: #002f42;
    font-weight: 600;
  }
  .btn-outline-secondary {
    background: #edf1f3;
    border-color: #cbd5da;
    color: #566670;
    border-radius: 3px;
    min-height: 35px;
    padding: 0px 16px;
  }
  .btn-outline-secondary:hover {
    background: #dfe7eb;
    border-color: #bac8ce;
  }
  .cs-muted-unavailable {
    color: #344b56; font-size: 14px;
  }
</style>



// WEBPACK FOOTER //
// src/components/admin/reports/customer_success_report.vue